import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { Icon, IconButton, IconName, styles as ui } from "stories/Admin";
import styles from "./PageModal.module.scss";

interface PageModalProps {
  open: boolean;
  title: React.ReactNode;
  icon?: IconName;
  onClose: () => void;
  footer?: React.ReactNode;
  width?: number;
  children: React.ReactNode;
}

/** Page-local dialog built with the admin library's modal classes. */
const PageModal: React.FC<PageModalProps> = ({
  open,
  title,
  icon,
  onClose,
  footer,
  width = 540,
  children,
}) => {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const first =
      ref.current?.querySelector<HTMLElement>("input, select, textarea") ??
      ref.current?.querySelector<HTMLElement>(`.${ui.mf} button`);
    first?.focus();
    return () => previous?.focus?.();
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className={ui.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        className={ui.modal}
        style={{ width, maxWidth: "calc(100vw - 32px)" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <h2 className={ui.mh} id={titleId}>
          {icon && (
            <span className={styles.icon} aria-hidden="true">
              <Icon name={icon} size={18} />
            </span>
          )}
          {title}
          <IconButton
            label="Lukk"
            size="sm"
            className={styles.close}
            icon={<Icon name="x" size={16} />}
            onClick={onClose}
          />
        </h2>
        <div className={ui.mb}>{children}</div>
        {footer && <div className={ui.mf}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};

export default PageModal;
